import { BaseService } from './BaseService';
import { API_ENDPOINTS } from '../config/api.config';
import type {
  ConversationAssociation,
  ConversationDetail,
  ConversationStats,
  ConversationApiResponse,
  CreateConversationAssociationResponse,
  UpdateConversationResponse,
  LockModelResponse,
} from '../types/conversation.types';

const sanitizeConversationDetail = (
  conversation: ConversationDetail | null
): ConversationDetail | null => {
  // Simply return the conversation as-is, including assistant_detail for all bots
  // We need to preserve assistant_detail even for default bots to show the correct bot in UI
  return conversation;
};

export class ConversationService extends BaseService {
  /**
   * Create a conversation-assistant association after a chat stream completes
   * @param conversationId - The UUID of the conversation from LLM service
   * @param assistantId - The numeric ID of the assistant
   */
  async createConversationAssociation(
    conversationId: string,
    assistantId: number
  ): Promise<ConversationDetail | null> {
    try {
      // Retry logic with exponential backoff
      const maxRetries = 3;
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const payload: ConversationAssociation = {
            conversation_id: conversationId,
            assistant: assistantId,
          };

          // Try to get the response with the new format first
          const response = await this.post<
            CreateConversationAssociationResponse | ConversationDetail
          >(API_ENDPOINTS.CONVERSATION_CREATE, payload);

          // Check if it's the new format with recent_assistants
          if (response && 'recent_assistants' in response) {
            const apiResponse =
              response as CreateConversationAssociationResponse;
            return sanitizeConversationDetail(apiResponse.conversation);
          }

          // Fallback to old format (backward compatibility)
          const conversationDetail = response as ConversationDetail;
          return conversationDetail;
        } catch (error) {
          lastError = error as Error;
          // If it's not the last attempt, wait before retrying
          if (attempt < maxRetries) {
            const delay = Math.pow(2, attempt - 1) * 1000; // Exponential backoff: 1s, 2s, 4s
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      // All retries failed
      console.error(
        '[ConversationService] Failed to create conversation association after all retries:',
        lastError
      );
      return null;
    } catch (error) {
      console.error(
        '[ConversationService] Unexpected error creating conversation association:',
        error
      );
      return null;
    }
  }

  /**
   * Get conversation details including associated assistant configuration
   * @param conversationId - The UUID of the conversation
   */
  async getConversationDetail(
    conversationId: string
  ): Promise<ConversationDetail | null> {
    try {
      const response = await this.get<
        ConversationApiResponse | ConversationDetail
      >(API_ENDPOINTS.CONVERSATION_DETAIL(conversationId));

      // Check if it's the new format with recent_assistants
      if (response && 'recent_assistants' in response) {
        const apiResponse = response as ConversationApiResponse;
        return sanitizeConversationDetail(apiResponse.conversation);
      }

      // Fallback to old format (backward compatibility)
      const conversationDetail = response as ConversationDetail;

      return sanitizeConversationDetail(conversationDetail);
    } catch (error) {
      console.error(
        '[ConversationService] Failed to get conversation detail:',
        error
      );
      return null;
    }
  }

  /**
   * Update conversation's last_conversation timestamp when continuing an existing conversation
   * @param conversationId - The UUID of the conversation
   */
  async updateConversation(
    conversationId: string
  ): Promise<ConversationDetail | null> {
    try {
      // Retry logic with exponential backoff
      const maxRetries = 3;
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          // GET request to update_conversation endpoint
          const response = await this.get<UpdateConversationResponse>(
            API_ENDPOINTS.CONVERSATION_UPDATE(conversationId)
          );

          if (response) {
            return response.conversation;
          }

          return null;
        } catch (error) {
          lastError = error as Error;
          // If it's not the last attempt, wait before retrying
          if (attempt < maxRetries) {
            const delay = Math.pow(2, attempt - 1) * 1000; // Exponential backoff: 1s, 2s, 4s
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      // All retries failed
      console.error(
        '[ConversationService] Failed to update conversation after all retries:',
        lastError
      );
      return null;
    } catch (error) {
      console.error(
        '[ConversationService] Unexpected error updating conversation:',
        error
      );
      return null;
    }
  }

  /**
   * Lock a specific AI model for a multimodal conversation
   * Once locked, this model should be used for all subsequent messages in the conversation
   * @param conversationId - The UUID of the conversation
   * @param modelId - The ID of the model to lock
   */
  async lockModel(
    conversationId: string,
    modelId: number
  ): Promise<ConversationDetail | null> {
    try {
      const response = await this.patch<LockModelResponse>(
        API_ENDPOINTS.CONVERSATION_LOCK_MODEL(conversationId),
        { model_id: modelId }
      );

      if (response) {
        return response.conversation;
      }

      return null;
    } catch (error) {
      console.error(
        '[ConversationService] Failed to lock model for conversation:',
        error
      );
      return null;
    }
  }

  /**
   * Get conversation statistics for assistants
   * @param assistantId - Optional assistant ID to filter statistics
   */
  async getConversationStats(
    assistantId?: number
  ): Promise<ConversationStats[]> {
    try {
      const url = assistantId
        ? `${API_ENDPOINTS.CONVERSATION_STATS}?assistant_id=${assistantId}`
        : API_ENDPOINTS.CONVERSATION_STATS;

      const response = await this.get<ConversationStats[]>(url);
      return response || [];
    } catch (error) {
      console.error(
        '[ConversationService] Failed to get conversation stats:',
        error
      );
      return [];
    }
  }

  /**
   * List conversations with optional filtering
   * @param filters - Optional filters for conversations
   */
  async listConversations(filters?: {
    assistant_id?: number;
    user_id?: number;
    created_after?: string;
    created_before?: string;
  }): Promise<ConversationDetail[]> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, String(value));
          }
        });
      }

      const url = params.toString()
        ? `${API_ENDPOINTS.CONVERSATION_CREATE.replace('/create_conversation/', '/')}?${params.toString()}`
        : API_ENDPOINTS.CONVERSATION_CREATE.replace(
            '/create_conversation/',
            '/'
          );

      const response = await this.get<ConversationDetail[]>(url);
      return response || [];
    } catch (error) {
      console.error(
        '[ConversationService] Failed to list conversations:',
        error
      );
      return [];
    }
  }
}

export const conversationService = new ConversationService();
